import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ChevronDown, ChevronRight, MoreHorizontal, ExternalLink, MoveRight } from "lucide-react";
import { toast } from "sonner";

export const milestoneOf = (t) => t.milestone_requirement_id || t.resolved_requirement_id || t.requirement_id || null;
const fmt = (d) => (d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");

/** Program → Phase → Milestone → Tasks, using the enrollment's phases/requirements for structure. */
export function groupTasks(items, enr) {
  const phases = (enr?.phases || []).slice().sort((a, b) => (a.order ?? a.display_order ?? 0) - (b.order ?? b.display_order ?? 0));
  const reqIndex = {};
  phases.forEach((p) => (p.requirements || []).forEach((r) => { reqIndex[r.requirement_id] = { phase: p, req: r }; }));
  const out = phases.map((p) => ({ id: p.id, name: p.name, is_current: p.is_current, order: p.order ?? p.display_order ?? 0, milestones: (p.requirements || []).map((r) => ({ id: r.requirement_id, title: r.title, tasks: [] })) }));
  const other = { id: "__other", name: "Other Tasks", is_current: false, order: 999, milestones: [] };
  const ad = { id: "__ad", name: "Agent Development (after Career appointment)", is_current: false, order: 1000, separate: true, milestones: [] };
  const otherMs = {};
  items.forEach((t) => {
    if (t.agent_development) {
      const k = t.ad_section || "Agent Development";
      if (!otherMs[`ad_${k}`]) { otherMs[`ad_${k}`] = { id: `__ad_${k}`, title: k, tasks: [] }; ad.milestones.push(otherMs[`ad_${k}`]); }
      otherMs[`ad_${k}`].tasks.push(t); return;
    }
    const hit = reqIndex[milestoneOf(t)];
    if (hit) { out.find((p) => p.id === hit.phase.id).milestones.find((m) => m.id === hit.req.requirement_id).tasks.push(t); return; }
    const k = t.project_name || "General";
    if (!otherMs[k]) { otherMs[k] = { id: `__o_${k}`, title: k, tasks: [] }; other.milestones.push(otherMs[k]); }
    otherMs[k].tasks.push(t);
  });
  const all = [...out, ...(other.milestones.length ? [other] : []), ...(ad.milestones.length ? [ad] : [])];
  const cur = all.find((p) => p.is_current)?.order ?? 0;
  all.forEach((p) => { p.upcoming = !p.id.startsWith("__") && p.order > cur; p.milestones = p.milestones.filter((m) => m.tasks.length); });
  return all.filter((p) => p.milestones.length);
}

export const progressOf = (tasks) => {
  const applicable = tasks.filter((t) => !["not_applicable", "cancelled"].includes(t.status));
  const done = applicable.filter((t) => t.status === "completed").length;
  return { done, total: applicable.length, pct: applicable.length ? Math.round((done / applicable.length) * 100) : 0 };
};

export const progressMap = (items, enr) => {
  const out = {};
  groupTasks(items, enr).forEach((p) => { out[p.id] = progressOf(p.milestones.flatMap((m) => m.tasks)); p.milestones.forEach((m) => { out[m.id] = progressOf(m.tasks); }); });
  return out;
};

export function MoveDialog({ task, enr, onClose, onMoved }) {
  const [v, setV] = useState("");
  const cur = milestoneOf(task);
  const save = async () => {
    try { await api.post(`/tasks/${task.id}/move-milestone`, { requirement_id: v }); toast.success("Task moved"); onMoved(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-md" data-testid="task-move-dialog">
        <DialogHeader><DialogTitle>Move to Milestone</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">"{task.title}" — only this candidate's task moves. Status, assignee, dates, documents and history stay the same.</p>
        <Select value={v} onValueChange={setV}>
          <SelectTrigger data-testid="task-move-select"><SelectValue placeholder="Choose a milestone" /></SelectTrigger>
          <SelectContent className="bg-white max-h-80">
            {(enr?.phases || []).map((p) => (
              <SelectGroup key={p.id}><SelectLabel className="text-[11px] uppercase text-slate-400">{p.name}</SelectLabel>
                {(p.requirements || []).map((r) => <SelectItem key={r.requirement_id} value={r.requirement_id} disabled={r.requirement_id === cur}>{r.title}{r.requirement_id === cur ? " (current)" : ""}</SelectItem>)}
              </SelectGroup>))}
          </SelectContent>
        </Select>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button disabled={!v} onClick={save} className="bg-brand hover:bg-brand-strong" data-testid="task-move-confirm">Move task</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function RowMenu({ t, canMove, onMove }) {
  const navigate = useNavigate();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild><Button size="icon" variant="ghost" className="h-8 w-8" aria-label="Task actions" data-testid={`task-row-menu-${t.id}`}><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-white">
        <DropdownMenuItem onClick={() => navigate(`/tasks/detail/${t.id}`)} data-testid={`task-open-${t.id}`}><ExternalLink className="w-4 h-4 mr-2" />Open task</DropdownMenuItem>
        {canMove && t.program_enrollment_id && <DropdownMenuItem onClick={() => onMove(t)} data-testid={`task-move-${t.id}`}><MoveRight className="w-4 h-4 mr-2" />Move to Milestone</DropdownMenuItem>}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function PhaseSection({ p, open, onToggle, renderRow, prog = {} }) {
  const all = p.milestones.flatMap((m) => m.tasks);
  const pr = prog[p.id] || progressOf(all);
  return (
    <section className="rounded-lg border border-slate-200 overflow-hidden" data-testid={`task-phase-${p.id}`}>
      <button type="button" onClick={onToggle} className="w-full flex items-center gap-2 bg-slate-50 px-3 py-2 text-left" data-testid={`task-phase-toggle-${p.id}`}>
        {open ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
        <span className="font-semibold text-sm text-slate-800 flex-1">{p.name}{p.is_current && <span className="ml-2 rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-medium text-sky-800">Current</span>}{p.upcoming && <span className="ml-2 rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-medium text-slate-600">Upcoming</span>}</span>
        <span className="text-xs text-slate-500" data-testid={`task-phase-progress-${p.id}`}>{pr.done}/{pr.total} · {pr.pct}%</span>
        <span className="hidden sm:block w-24 h-1.5 rounded-full bg-slate-200 overflow-hidden"><span className="block h-full bg-emerald-500" style={{ width: `${pr.pct}%` }} /></span>
      </button>
      {open && p.milestones.map((m) => (
        <div key={m.id} data-testid={`task-milestone-${m.id}`}>
          <p className="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400 border-t border-slate-100">{m.title} <span className="font-normal normal-case">· {(prog[m.id] || progressOf(m.tasks)).done}/{(prog[m.id] || progressOf(m.tasks)).total}</span></p>
          <div className="divide-y divide-slate-100">{m.tasks.map(renderRow)}</div>
        </div>
      ))}
    </section>
  );
}

export const fmtDate = fmt;
